import { useRef, useState, type KeyboardEvent } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { SheetModel } from "../lib/sheetModel";
import { SheetCourtHead } from "./SheetCourtHead";
import { SheetCell } from "./SheetCell";

function cellKeyOf(row: number, column: number): string {
  return `r${row}c${column}`;
}

/** Horizontally scrollable court-by-slot grid with roving keyboard focus. */
export function CourtSheet({
  model,
  estimate,
  currency = "USD",
  isSelected,
  onToggleSlot,
}: {
  model: SheetModel;
  estimate: number | null;
  currency?: string;
  isSelected: (courtId: string, startTime: string) => boolean;
  onToggleSlot: (courtId: string, startTime: string) => void;
}) {
  const { columns, rows } = model;
  const gridRef = useRef<HTMLDivElement>(null);
  const [activeRaw, setActive] = useState({ r: 0, c: 0 });
  const rowCount = rows.length;
  const colCount = columns.length;
  const active = {
    r: Math.min(activeRaw.r, Math.max(0, rowCount - 1)),
    c: Math.min(activeRaw.c, Math.max(0, colCount - 1)),
  };
  // Dense: several courts fit a phone; the time column stays pinned.
  const gridTemplateColumns = `56px repeat(${columns.length}, minmax(64px, 1fr))`;
  const gridMinWidth = 56 + columns.length * 64;

  function focusCell(row: number, column: number) {
    const element = gridRef.current?.querySelector<HTMLElement>(
      `[data-cellkey="${cellKeyOf(row, column)}"]`,
    );
    if (!element) return;
    setActive({ r: row, c: column });
    element.focus();
  }

  function onGridKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (rowCount === 0 || colCount === 0) return;
    let { r, c } = active;
    switch (event.key) {
      case "ArrowDown":
        r = Math.min(rowCount - 1, r + 1);
        break;
      case "ArrowUp":
        r = Math.max(0, r - 1);
        break;
      case "ArrowRight":
        c = Math.min(colCount - 1, c + 1);
        break;
      case "ArrowLeft":
        c = Math.max(0, c - 1);
        break;
      case "Home":
        c = 0;
        break;
      case "End":
        c = colCount - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    focusCell(r, c);
  }

  return (
    <Box
      data-testid="court-sheet-scroll"
      sx={{ overflowX: "auto", pb: 1, WebkitOverflowScrolling: "touch" }}
    >
      <Box
        ref={gridRef}
        role="grid"
        aria-label={`Court availability for ${model.date}, venue-local time — tap an open slot to add it to your cart`}
        onKeyDown={onGridKeyDown}
        sx={{ minWidth: gridMinWidth }}
      >
        <Box
          role="row"
          data-testid="court-sheet-header"
          sx={{
            display: "grid",
            gridTemplateColumns,
            position: "sticky",
            top: 0,
            zIndex: 3,
            bgcolor: "background.default",
          }}
        >
          <Box
            aria-hidden="true"
            data-testid="court-sheet-corner"
            sx={{
              position: "sticky",
              left: 0,
              zIndex: 4,
              bgcolor: "background.default",
            }}
          />
          {columns.map((column) => (
            <Box
              key={column.courtId}
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              <SheetCourtHead column={column} />
            </Box>
          ))}
        </Box>

        {rows.map((row, rowIdx) => (
          <Box
            key={row.startTime}
            role="row"
            sx={{ display: "grid", gridTemplateColumns }}
          >
            <Typography
              component="div"
              role="rowheader"
              data-testid="court-sheet-time"
              variant="caption"
              sx={{
                position: "sticky",
                left: 0,
                zIndex: 2,
                display: "grid",
                placeItems: "center",
                bgcolor: "background.default",
                borderBottom: 1,
                borderColor: "divider",
                fontWeight: 600,
                fontSize: "0.6875rem",
                color: "text.secondary",
              }}
            >
              {row.label}
            </Typography>
            {row.cells.map((cell, colIdx) => {
              const column = columns[colIdx];
              const isActive = active.r === rowIdx && active.c === colIdx;
              return (
                <Box
                  key={cell.courtId}
                  role="gridcell"
                  sx={{ p: 0.25, borderBottom: 1, borderColor: "divider" }}
                >
                  <SheetCell
                    cell={cell}
                    courtName={column.name}
                    hourLabel={row.label}
                    estimate={estimate}
                    currency={currency}
                    cellKey={cellKeyOf(rowIdx, colIdx)}
                    tabIndex={isActive ? 0 : -1}
                    selected={
                      cell.slot
                        ? isSelected(cell.courtId, cell.slot.start_time)
                        : false
                    }
                    onToggle={() => onToggleSlot(cell.courtId, row.startTime)}
                  />
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
